/**
 * 敏感词检测工具（重构版）
 *
 * 改动说明：
 * - 词库来源从本地 sensitiveWords.json 改为线上 API 动态获取
 * - 匹配引擎从线性扫描改为 Trie 前缀树，解决大量词库的性能问题
 * - 对外 API 接口保持不变，所有页面无需任何修改
 *
 * 功能：
 * 1. 从线上 API 实时加载敏感词库（每次启动重新获取，不缓存）
 * 2. 构建 Trie 前缀树实现 O(text_len) 高性能匹配
 * 3. 提供 Element Plus 表单验证器（输入时红字提示）
 * 4. 提供保存前统一检测和弹窗二次确认
 */
import { ElMessageBox } from 'element-plus'
import SensitiveWordTrie from './sensitiveWordTrie'
import { getSensitiveWords } from '@/api/sensitive'
import sensitiveWordsFallback from './sensitiveWords.json'

// ============= 引擎状态 =============

let trie = null       // Trie 实例
let initPromise = null // 初始化 Promise（避免并发重复初始化）
let isReady = false   // 引擎是否就绪

// ============= 初始化 =============

/**
 * 初始化敏感词检测引擎
 * - 每次启动从 API 实时获取词库，不做缓存
 * - 构建 Trie 前缀树
 *
 * 调用时机：App 启动时在 main.js 中调用一次
 * 设计为 fire-and-forget：不阻塞页面渲染
 *
 * @returns {Promise<void>}
 */
export async function initSensitiveEngine() {
    // 避免并发重复初始化
    if (initPromise) return initPromise

    initPromise = _doInit()
    return initPromise
}

async function _doInit() {
    try {
        const words = await getSensitiveWords()

        if (words && words.length > 0) {
            trie = new SensitiveWordTrie()
            trie.build(words)
            isReady = true
            return
        }
    } catch (error) {
        // API 请求失败，降级使用本地 JSON 兜底
    }

    // 兜底：接口失败或返回为空时，使用本地 JSON 文件
    try {
        const list = Array.isArray(sensitiveWordsFallback)
            ? sensitiveWordsFallback
            : sensitiveWordsFallback?.default || []
        if (list.length > 0) {
            trie = new SensitiveWordTrie()
            trie.build(list)
            isReady = true
        }
    } catch (fallbackError) {
        // JSON 兜底也失败，敏感词检测将不可用
    }
}

/**
 * 检查引擎是否就绪
 * @returns {boolean}
 */
export function isEngineReady() {
    return isReady && trie !== null
}

/**
 * 手动刷新词库（从 API 重新获取）
 * @returns {Promise<void>}
 */
export async function refreshSensitiveWords() {
    try {
        const words = await getSensitiveWords()
        if (words && words.length > 0) {
            trie = new SensitiveWordTrie()
            trie.build(words)
            isReady = true
            return
        }
    } catch (error) {
        // API 请求失败，降级使用本地 JSON 兜底
    }

    // 兜底
    const list = Array.isArray(sensitiveWordsFallback)
        ? sensitiveWordsFallback
        : sensitiveWordsFallback?.default || []
    if (list.length > 0) {
        trie = new SensitiveWordTrie()
        trie.build(list)
        isReady = true
    }
}

// ============= 核心检测函数（接口与旧版完全一致）=============

/**
 * 去除 HTML 标签，提取纯文本
 * @param {string} html - HTML 字符串
 * @returns {string} 纯文本
 */
export function stripHtmlTags(html) {
    if (!html) return ''
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * 检测单个文本是否包含敏感词
 *
 * 引擎未就绪时返回空数组（静默跳过检测）
 *
 * @param {string} text - 要检测的文本
 * @returns {string[]} 匹配到的敏感词数组（原始大小写形式）
 */
export function checkSensitiveWords(text) {
    if (!isReady || !trie) return []
    return trie.search(text, 20)
}

/**
 * 批量检测多个字段
 * @param {Object} fields - { fieldLabel: fieldValue } 的映射
 * @returns {{ hasIssue: boolean, results: Array<{field: string, words: string[]}> }}
 */
export function checkFormFields(fields) {
    const results = []

    for (const [fieldLabel, value] of Object.entries(fields)) {
        if (!value) continue
        const text = typeof value === 'string' ? value : String(value)
        const words = checkSensitiveWords(text)
        if (words.length > 0) {
            results.push({ field: fieldLabel, words })
        }
    }

    return {
        hasIssue: results.length > 0,
        results
    }
}

// ============= Element Plus 表单验证器 =============

/**
 * 创建 Element Plus 表单验证用的自定义 validator
 *
 * 行为：输入框中实时检测，命中敏感词时在字段下方显示红字提示，
 * 但允许继续输入（不阻止表单提交）。
 *
 * @returns {Function} validator 函数
 */
export function createSensitiveWordValidator() {
    return (rule, value, callback) => {
        if (!value) {
            callback()
            return
        }

        const words = checkSensitiveWords(value)
        if (words.length > 0) {
            const displayWords = words.slice(0, 3).join('、')
            const suffix = words.length > 3 ? ` 等${words.length}个` : ''
            callback(new Error(`温馨提示：当前输入包含敏感词：${displayWords}${suffix}，但您可以继续保存`))
        } else {
            callback()
        }
    }
}

// ============= 表单校验辅助 =============

/**
 * 判断表单校验错误是否全部为敏感词"温馨提示"类错误
 * 用于在 el-form validate 回调中，若只有敏感词警告则放行进入弹窗流程
 * @param {Object} errors - el-form validate 回调的第二个参数 (invalidFields)
 * @returns {boolean} true=全部为敏感词警告，可以放行进入弹窗流程
 */
export function isOnlySensitiveWordErrors(errors) {
    if (!errors) return false
    for (const field in errors) {
        const fieldErrors = errors[field]
        for (const err of fieldErrors) {
            if (!err.message || !err.message.startsWith('温馨提示')) {
                return false
            }
        }
    }
    return true
}

// ============= 保存前检测弹窗 =============

/**
 * 保存前统一检测，如发现敏感词则弹窗警告
 * 用户可选择"继续保存"（返回 true）或"再去修改"（返回 false）
 *
 * @param {Object} fields - { fieldLabel: fieldValue } 需检测的字段
 * @param {string} [richTextContent] - 富文本内容（HTML）
 * @returns {Promise<boolean>} true=通过检测可提交, false=检测不通过已阻止
 */
export async function validateBeforeSave(fields, richTextContent) {
    const allFields = { ...fields }
    if (richTextContent) {
        allFields['富文本内容'] = stripHtmlTags(richTextContent)
    }

    const { hasIssue, results } = checkFormFields(allFields)

    if (!hasIssue) {
        return true
    }

    const messages = results.map(r => {
        const displayWords = r.words.slice(0, 5).join('、')
        const suffix = r.words.length > 5 ? ` 等${r.words.length}个` : ''
        return `【${r.field}】包含敏感词：${displayWords}${suffix}`
    })

    try {
        await ElMessageBox.confirm(
            `<div style="max-height: 300px; overflow-y: auto;">
          <p style="color: #E6A23C; font-weight: bold; margin-bottom: 10px;">⚠️ 检测到敏感词汇，是否继续保存？</p>
          ${messages.map(m => `<p style="margin: 5px 0; color: #F56C6C;">• ${m}</p>`).join('')}
        </div>`,
            '敏感词检测警告',
            {
                dangerouslyUseHTMLString: true,
                confirmButtonText: '继续保存',
                cancelButtonText: '再去修改',
                type: 'warning'
            }
        )
        return true
    } catch (error) {
        return false
    }
}

// ============= 别名兼容 =============

export const showSensitiveWordWarning = validateBeforeSave

export default {
    initSensitiveEngine,
    isEngineReady,
    refreshSensitiveWords,
    checkSensitiveWords,
    checkFormFields,
    stripHtmlTags,
    createSensitiveWordValidator,
    isOnlySensitiveWordErrors,
    validateBeforeSave,
    showSensitiveWordWarning
}
